<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Transition name="modal-fade">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="handleClose"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden transform transition-all">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-slate-900">{{ title }}</h3>
                <p class="text-xs text-slate-500">{{ description }}</p>
              </div>
            </div>
            <button @click="handleClose" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 产品树内容 -->
          <div class="overflow-y-auto max-h-[calc(80vh-140px)] p-6">
            <div v-if="props.productTree.length === 0" class="text-center py-12">
              <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </div>
              <p class="text-slate-500">暂无可用产品</p>
            </div>

            <div v-else class="space-y-4">
              <div v-for="category in props.productTree" :key="category.id" class="border border-slate-200 rounded-xl overflow-hidden">
                <!-- 类目头部 -->
                <div class="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <el-checkbox
                      :model-value="isCategoryFullySelected(category)"
                      :indeterminate="isCategoryPartiallySelected(category)"
                      @change="(val) => toggleCategoryProducts(category, val)"
                    >
                      <span class="font-medium text-slate-700">{{ category.name }}</span>
                      <span class="text-xs text-slate-400 ml-2">({{ category.products?.length || 0 }}个产品)</span>
                    </el-checkbox>
                  </div>
                  <el-tag v-if="category.status === 0" type="info" size="small">停用</el-tag>
                </div>
                <!-- 产品列表 -->
                <div v-if="category.products && category.products.length > 0" class="divide-y divide-slate-100">
                  <div
                    v-for="product in category.products"
                    :key="product.id"
                    class="px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between"
                  >
                    <div class="flex items-center gap-3">
                      <el-checkbox
                        :model-value="selectedIds.includes(product.id)"
                        @change="() => handleProductSelect(product.id)"
                      >
                        <div class="flex flex-col">
                          <span class="text-sm text-slate-700">{{ product.name }}</span>
                          <span v-if="product.shortName" class="text-xs text-slate-400">{{ product.shortName }}</span>
                        </div>
                      </el-checkbox>
                    </div>
                    <div class="flex items-center gap-4">
                      <span v-if="product.priceDisplay" class="text-sm font-medium text-primary-600">{{ product.priceDisplay }}</span>
                      <el-tag v-if="product.status === 0" type="info" size="small">下架</el-tag>
                      <el-tag v-else type="success" size="small">上架</el-tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部 -->
          <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
            <div class="text-sm text-slate-600">
              已选择 <span class="font-semibold text-primary-600">{{ selectedIds.length }}</span> 个产品
            </div>
            <div class="flex items-center gap-3">
              <button
                @click="clearSelection"
                class="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors text-sm"
              >
                清除选择
              </button>
              <button
                @click="selectAll"
                class="px-4 py-2 text-primary-600 hover:text-primary-700 transition-colors text-sm"
              >
                全选全部
              </button>
              <button
                @click="handleConfirm"
                class="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 shadow-lg shadow-primary-500/25 transition-all duration-200 font-medium"
              >
                确定 ({{ selectedIds.length }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '选择授权产品'
  },
  description: {
    type: String,
    default: '选择要授权的产品'
  },
  productTree: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible', 'update:modelValue', 'confirm'])

// 选中产品
const selectedIds = ref([])

// 监听 modelValue 变化
watch(() => props.modelValue, (val) => {
  selectedIds.value = [...val]
}, { immediate: true })

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false)
}

// 确认选择
const handleConfirm = () => {
  emit('update:modelValue', [...selectedIds.value])
  emit('confirm', [...selectedIds.value])
  handleClose()
}

// 全选/取消全选类目下的产品
const toggleCategoryProducts = (category, checked) => {
  const productIds = category.products.map(p => p.id)
  if (checked) {
    productIds.forEach(id => {
      if (!selectedIds.value.includes(id)) {
        selectedIds.value.push(id)
      }
    })
  } else {
    selectedIds.value = selectedIds.value.filter(id => !productIds.includes(id))
  }
}

// 检查是否全选某个类目
const isCategoryFullySelected = (category) => {
  if (!category.products || category.products.length === 0) return false
  return category.products.every(p => selectedIds.value.includes(p.id))
}

// 检查是否部分选中某个类目
const isCategoryPartiallySelected = (category) => {
  if (!category.products || category.products.length === 0) return false
  const selectedCount = category.products.filter(p => selectedIds.value.includes(p.id)).length
  return selectedCount > 0 && selectedCount < category.products.length
}

// 单个产品选择/取消选择
const handleProductSelect = (productId) => {
  const index = selectedIds.value.indexOf(productId)
  if (index > -1) {
    selectedIds.value.splice(index, 1)
  } else {
    selectedIds.value.push(productId)
  }
}

// 清除所有选择
const clearSelection = () => {
  selectedIds.value = []
}

// 全选所有产品
const selectAll = () => {
  props.productTree.forEach(category => {
    if (category.products) {
      category.products.forEach(product => {
        if (!selectedIds.value.includes(product.id)) {
          selectedIds.value.push(product.id)
        }
      })
    }
  })
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .relative,
.modal-fade-leave-to .relative {
  transform: scale(0.95) translateY(-10px);
}

.modal-fade-enter-active .relative,
.modal-fade-leave-active .relative {
  transition: all 0.3s ease;
}
</style>
